import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardContent } from "../ui/Card";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Plus } from "lucide-react";
import { fetchCollaboratorOptions } from "../../services/collaborators";
import { getAssetLocations } from "../../services/assetLocations";
import LocationCatalogModal from "./LocationCatalogModal";

const EditAssetModal = ({ isOpen, onClose, asset, onSubmit, onDelete }) => {
  const [collaborators, setCollaborators] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locModalOpen, setLocModalOpen] = useState(false);
  const loadLocations = () => getAssetLocations().then(setLocations).catch(() => setLocations([]));
  const [form, setForm] = useState({
    asset_type: "",
    brand: "",
    model: "",
    hostname: "",
    serial_number: "",
    patrimony_code: "",
    invoice_number: "",
    status: "Ativo",
    location: "",
    collaborator_id: "",
    notes: "",
  });

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;

    const loadCollaborators = async () => {
      const items = await fetchCollaboratorOptions();
      if (isMounted) setCollaborators(items);
    };

    loadCollaborators();
    loadLocations();

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!asset) return;

    setForm({
      asset_type: asset.asset_type || asset.type || "",
      brand: asset.brand || "",
      model: asset.model || "",
      hostname: asset.hostname || "",
      serial_number: asset.serial_number || "",
      patrimony_code: asset.patrimony_code || "",
      invoice_number: asset.invoice_number || "",
      status: asset.status || "Ativo",
      location: asset.location || "",
      collaborator_id: asset.collaborator_id || "",
      notes: asset.notes || asset.observations || "",
    });
  }, [asset]);

  if (!isOpen) return null;

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => onSubmit(form);

  const handleDelete = () => {
    if (!asset?.id) return;
    const ok = window.confirm(`Tem certeza que deseja excluir o ativo #${asset.id}?`);
    if (ok) onDelete(asset.id);
  };

  const formatCollaboratorLabel = (collaborator) => {
    if (!collaborator) return "Sem nome";

    const name = `${collaborator.first_name || ""} ${collaborator.last_name || ""}`.trim();
    return name || "Sem nome";
  };

  const collaboratorOptions = [...collaborators];
  if (
    asset?.collaborator_id &&
    asset?.collaborator_name &&
    !collaboratorOptions.some(
      (collaborator) => String(collaborator.id) === String(asset.collaborator_id)
    )
  ) {
    collaboratorOptions.unshift({
      id: asset.collaborator_id,
      first_name: asset.collaborator_name,
      last_name: "",
    });
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3 sm:p-4">
      <div className="w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Editar Ativo #{asset?.id}
                </h2>
                <p className="text-sm text-gray-500">
                  Atualize os campos e salve. Para excluir, use o botão “Deletar”.
                </p>
              </div>
              <Button variant="outline" onClick={onClose}>
                Fechar
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600">Tipo</label>
                <Input
                  value={form.asset_type}
                  onChange={(e) => handleChange("asset_type", e.target.value)}
                  placeholder="Ex: Notebook"
                />
              </div>

              <div>
                <label className="text-xs text-gray-600">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="Ativo">Ativo</option>
                  <option value="Em Estoque">Em Estoque</option>
                  <option value="Inativo">Inativo</option>
                  <option value="Em Manutenção">Em Manutenção</option>
                  <option value="Baixado">Baixado</option>
                </select>
              </div>

              <div>
                <label className="text-xs text-gray-600">Marca</label>
                <Input
                  value={form.brand}
                  onChange={(e) => handleChange("brand", e.target.value)}
                  placeholder="Ex: Dell"
                />
              </div>

              <div>
                <label className="text-xs text-gray-600">Modelo</label>
                <Input
                  value={form.model}
                  onChange={(e) => handleChange("model", e.target.value)}
                  placeholder="Ex: Latitude 3540"
                />
              </div>

              <div>
                <label className="text-xs text-gray-600">Hostname</label>
                <Input
                  value={form.hostname}
                  onChange={(e) => handleChange("hostname", e.target.value)}
                  placeholder="Ex: NB-TI-001"
                />
              </div>

              <div>
                <label className="text-xs text-gray-600">Número de Série</label>
                <Input
                  value={form.serial_number}
                  onChange={(e) => handleChange("serial_number", e.target.value)}
                />
              </div>

              <div>
                <label className="text-xs text-gray-600">Código Patrimonial</label>
                <Input
                  value={form.patrimony_code}
                  onChange={(e) => handleChange("patrimony_code", e.target.value)}
                />
              </div>

              <div>
                <label className="text-xs text-gray-600">Nota Fiscal</label>
                <Input
                  value={form.invoice_number}
                  onChange={(e) => handleChange("invoice_number", e.target.value)}
                  placeholder="Número da NF de entrada"
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs text-gray-600">Colaborador vinculado</label>
                <select
                  value={form.collaborator_id}
                  onChange={(e) => handleChange("collaborator_id", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Não vincular</option>
                  {collaboratorOptions.map((collaborator) => (
                    <option key={collaborator.id} value={collaborator.id}>
                      {formatCollaboratorLabel(collaborator)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-600">Localização</label>
                  <button
                    type="button"
                    onClick={() => setLocModalOpen(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    <Plus size={14} /> Cadastrar localização
                  </button>
                </div>
                <select
                  value={form.location || ""}
                  onChange={(e) => handleChange("location", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Selecione uma localização...</option>
                  {form.location &&
                    !locations.some((l) => l.name === form.location) && (
                      <option value={form.location}>{form.location} (atual)</option>
                    )}
                  {locations.map((l) => (
                    <option key={l.id} value={l.name}>{l.name}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="text-xs text-gray-600">Observações</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  className="w-full min-h-[90px] px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="Informações adicionais"
                />
              </div>
            </div>

            <div className="flex flex-col gap-3 mt-5 sm:flex-row sm:items-center sm:justify-between">
              <Button
                variant="outline"
                onClick={handleDelete}
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                Deletar
              </Button>

              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <Button variant="outline" onClick={onClose}>
                  Cancelar
                </Button>
                <Button onClick={handleSave}>Salvar Alterações</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <LocationCatalogModal
        isOpen={locModalOpen}
        onClose={() => setLocModalOpen(false)}
        onChanged={loadLocations}
      />
    </div>
  );
};

export default EditAssetModal;
